import { z } from "zod";

/** Campo libre clave/valor (embebido, no colección). */
export const customFieldSchema = z.object({
  key: z.string().min(1, "La clave es requerida"),
  value: z.string(),
});

export type ICustomField = z.infer<typeof customFieldSchema>;

/** Endpoint DICOM embebido en software_instances. */
export const dicomEndpointSchema = z.object({
  aetitle: z.string().min(1, "AETitle requerido"),
  port: z.coerce.number().int().min(1).max(65535),
  ip: z.string().optional(),
  label: z.string().optional(),
});

export type IDicomEndpoint = z.infer<typeof dicomEndpointSchema>;

export const contactSchema = z.object({
  name: z.string().min(1, "Nombre requerido"),
  role: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
});

export type IContact = z.infer<typeof contactSchema>;

export const hostKindSchema = z.enum(["physical", "vm", "station"]);
export type HostKind = z.infer<typeof hostKindSchema>;

export const HOST_KIND_LABELS: Record<HostKind, string> = {
  physical: "Físico",
  vm: "VM",
  station: "Estación",
};

/** Catálogo global software_types. */
export const softwareTypeSchema = z.object({
  id: z.string(),
  key: z.string().min(1),
  displayName: z.string().min(1),
  active: z.boolean().default(true),
  order: z.number().int().default(0),
});

export type ISoftwareType = z.infer<typeof softwareTypeSchema>;

/**
 * Keys canónicos del catálogo (lowercase).
 * Misma paridad en infra, licencias y backend.
 */
export const SOFTWARE_TYPE_KEYS = [
  "pacs",
  "ris",
  "his",
  "router",
  "worklist",
  "visor",
  "laboratorio",
] as const;

export type SoftwareTypeKey = (typeof SOFTWARE_TYPE_KEYS)[number];

const SOFTWARE_TYPE_DISPLAY_NAMES: Record<SoftwareTypeKey, string> = {
  pacs: "PACS",
  ris: "RIS",
  his: "HIS",
  router: "Router",
  worklist: "Worklist",
  visor: "Visor",
  laboratorio: "Laboratorio",
};

/** Label UI para una key de catálogo (paridad licencias ↔ infra). */
export function softwareTypeLabel(key: string): string {
  if (key in SOFTWARE_TYPE_DISPLAY_NAMES) {
    return SOFTWARE_TYPE_DISPLAY_NAMES[key as SoftwareTypeKey];
  }
  return key;
}

export const DEFAULT_SOFTWARE_TYPES: Omit<ISoftwareType, "id">[] =
  SOFTWARE_TYPE_KEYS.map((key, index) => ({
    key,
    displayName: SOFTWARE_TYPE_DISPLAY_NAMES[key],
    active: true,
    order: index + 1,
  }));

/** Dirección operativa del centro (ficha infra). No altera users.address. */
export const infraAddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  zip: z.string().optional(),
});

export type IInfraAddress = z.infer<typeof infraAddressSchema>;

/** Cabecera center_infra/{institutionUid}. Sin DICOM. */
export const centerInfraSchema = z.object({
  institutionUid: z.string(),
  /** Dirección de la ficha de infra (independiente del perfil del usuario). */
  address: infraAddressSchema.optional(),
  contacts: z.array(contactSchema).default([]),
  publicIps: z.array(z.string()).default([]),
  customFields: z.array(customFieldSchema).default([]),
  updatedAt: z.string().optional(),
  createdAt: z.string().optional(),
});

export type ICenterInfra = z.infer<typeof centerInfraSchema>;

export const hostSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Nombre requerido"),
  kind: hostKindSchema.default("physical"),
  ip: z.string().optional(),
  anydeskId: z.string().optional(),
  anydeskPass: z.string().optional(),
  /** Etiqueta libre (ej. "Ubuntu 20.04 Desktop"). */
  os: z.string().optional(),
  /** Familia SO para política de soporte (ubuntu, windows, other). */
  osFamily: z.enum(["ubuntu", "windows", "other"]).optional(),
  /** Versión SO estructurada (ej. 20.04 para Ubuntu). */
  osVersion: z.string().optional(),
  osUser: z.string().optional(),
  osPass: z.string().optional(),
  hostname: z.string().optional(),
  hardware: z.string().optional(),
  /** Fecha de última verificación del equipo (YYYY-MM-DD). */
  verifiedAt: z.string().optional(),
  /** Quién verificó el equipo. */
  verifiedBy: z.string().optional(),
  customFields: z.array(customFieldSchema).default([]),
});

export type IHost = z.infer<typeof hostSchema>;

export type OsFamily = NonNullable<IHost["osFamily"]>;

export const OS_FAMILY_LABELS: Record<OsFamily, string> = {
  ubuntu: "Ubuntu Desktop",
  windows: "Windows",
  other: "Otro",
};

/** Política global de soporte de SO en servidores (viewmed_config.osSupportPolicy). */
export const osSupportPolicySchema = z.object({
  /** Familia principal que ViewMed despliega (casi siempre ubuntu). */
  family: z.enum(["ubuntu", "windows", "other"]).default("ubuntu"),
  /** Versión recomendada / más reciente (ej. 26.04). */
  recommended: z.string().min(1),
  /** Mínimo con el que aún se aceptan updates de software (ej. 20.04). */
  minSupported: z.string().min(1),
});

export type IOsSupportPolicy = z.infer<typeof osSupportPolicySchema>;

export const DEFAULT_OS_SUPPORT_POLICY: IOsSupportPolicy = {
  family: "ubuntu",
  recommended: "26.04",
  minSupported: "20.04",
};

/** Versiones Ubuntu habituales en el desplegable. */
export const COMMON_UBUNTU_VERSIONS = [
  "26.04",
  "24.04",
  "22.04",
  "20.04",
  "18.04",
] as const;

/** Estado de soporte SO del host vs política. */
export type OsSupportStatus =
  | "recommended"
  | "supported"
  | "unsupported"
  | "unknown";

export const OS_SUPPORT_STATUS_LABELS: Record<OsSupportStatus, string> = {
  recommended: "Al día (recomendado)",
  supported: "Soportado (recibe updates)",
  unsupported: "Sin soporte (actualizar SO)",
  unknown: "Sin clasificar",
};

/** Config global de revisión de infraestructura (viewmed_config). */
export const platformNotificationSchema = z.object({
  email: z.boolean().default(true),
  push: z.boolean().default(false),
  ws: z.boolean().default(true),
});

export type IPlatformNotification = z.infer<typeof platformNotificationSchema>;

export const DEFAULT_PLATFORM_NOTIFICATION: IPlatformNotification = {
  email: true,
  push: false,
  ws: true,
};

export const infraSettingsSchema = z.object({
  revisionIntervalMonths: z.number().int().min(1).max(60).default(6),
  updatedAt: z.string().optional(),
  /**
   * Versión objetivo por tipo de software (clave = software_types.key).
   * Nomenclatura: 0.0.0
   */
  softwareTargetVersions: z.record(z.string(), z.string()).default({}),
  /** Política de versiones SO en servidores (Ubuntu, etc.). */
  osSupportPolicy: osSupportPolicySchema.optional(),
  /** Emails placeholder / inválidos a ignorar (viewmed_config.black_list_email). */
  blackListEmail: z.array(z.string()).default([]),
  /** Canales de notificación globales (viewmed_config.notification). */
  notification: platformNotificationSchema.optional(),
});

export type IInfraSettings = z.infer<typeof infraSettingsSchema>;

export const DEFAULT_REVISION_INTERVAL_MONTHS = 6;

/** Versión semver mayor.menor.patch (ej. 1.2.0). */
export const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

/** Versión Ubuntu YY.MM (ej. 20.04). */
export const OS_VERSION_PATTERN = /^\d{2}\.\d{2}$/;

export const softwareInstanceSchema = z.object({
  id: z.string(),
  softwareTypeId: z.string().min(1),
  hostId: z.string().optional(),
  active: z.boolean().default(true),
  mail: z.string().optional(),
  password: z.string().optional(),
  url: z.string().optional(),
  /** Versión instalada (nomenclatura 0.0.0). */
  version: z.string().optional(),
  /** Fecha del último update (ISO date YYYY-MM-DD). */
  lastUpdatedAt: z.string().optional(),
  /** Fecha de última verificación del software (YYYY-MM-DD). */
  verifiedAt: z.string().optional(),
  /** Quién verificó el software. */
  verifiedBy: z.string().optional(),
  dicomEndpoints: z.array(dicomEndpointSchema).default([]),
  customFields: z.array(customFieldSchema).default([]),
});

export type ISoftwareInstance = z.infer<typeof softwareInstanceSchema>;

/** Fila aplanada para reportes de inventario. */
export interface ISoftwareInventoryRow {
  institutionUid: string;
  institutionName: string;
  institutionEmail?: string;
  instanceId: string;
  softwareTypeId: string;
  softwareKey?: string;
  softwareLabel: string;
  version?: string;
  targetVersion?: string;
  lastUpdatedAt?: string;
  verifiedAt?: string;
  verifiedBy?: string;
  active: boolean;
  hostId?: string;
  hostName?: string;
  hostIp?: string;
  hostVerifiedAt?: string;
  hostVerifiedBy?: string;
  url?: string;
}

/** Fila de host para verificación + soporte SO. */
export interface IHostVerificationRow {
  institutionUid: string;
  institutionName: string;
  institutionEmail?: string;
  hostId: string;
  hostName: string;
  hostKind: HostKind;
  hostIp?: string;
  anydeskId?: string;
  os?: string;
  osFamily?: OsFamily;
  osVersion?: string;
  osSupportStatus: OsSupportStatus;
  /** True si recommended o supported (aún se pueden aplicar updates). */
  acceptsSoftwareUpdates: boolean;
  recommendedOsVersion?: string;
  minSupportedOsVersion?: string;
  verifiedAt?: string;
  verifiedBy?: string;
}

export type CreateHostInput = Omit<IHost, "id">;
export type UpdateHostInput = Partial<CreateHostInput>;

export type CreateSoftwareInstanceInput = Omit<ISoftwareInstance, "id">;
export type UpdateSoftwareInstanceInput = Partial<CreateSoftwareInstanceInput>;

export type CreateSoftwareTypeInput = Omit<ISoftwareType, "id">;
export type UpdateSoftwareTypeInput = Partial<CreateSoftwareTypeInput>;

export type UpsertCenterInfraInput = {
  address?: IInfraAddress;
  contacts?: IContact[];
  publicIps?: string[];
  customFields?: ICustomField[];
};
